import { useMemo } from 'react';

/**
 * Derives P&L for each position based on current prices.
 * Pure computation — no side effects.
 */
export default function usePositionsWithPnl(positions, prices, trader) {
  return useMemo(() => {
    if (!trader) return { positionsWithPnl: [], totalPnl: 0, totalMargin: 0, equity: 0 };

    const md = Math.pow(10, trader.moneyDigits);

    const positionsWithPnl = positions.map(pos => {
      const price = prices[pos.symbolId];
      let pnl = 0, pnlPct = 0, currentPrice = 0;
      if (price && pos.entryPrice > 0) {
        currentPrice = pos.side === 'buy' ? price.bid : price.ask;
        const diff = pos.side === 'buy'
          ? currentPrice - pos.entryPrice
          : pos.entryPrice - currentPrice;
        const volumeUnits = pos.volume / 100;
        let rawPnl = diff * volumeUnits;
        // Convert P&L to USD for non-USD quoted pairs
        const sym = pos.symbol || '';
        if (sym.includes('JPY') && !sym.startsWith('USD')) {
          rawPnl = rawPnl / 159; // JPY cross → approximate USDJPY
        } else if (sym.startsWith('USD') && !sym.endsWith('USD')) {
          rawPnl = rawPnl / pos.entryPrice; // USDJPY, USDCAD etc → convert via entry
        }
        pnl = rawPnl;
        pnlPct = (diff / pos.entryPrice) * 100;
      }
      return { ...pos, currentPrice, unrealizedPnl: pnl, pnlPercent: pnlPct };
    });

    const totalPnl = positionsWithPnl.reduce((s, p) => s + p.unrealizedPnl, 0);
    const totalMargin = positions.reduce((s, p) => s + (p.usedMargin || 0), 0) / md;
    const equity = trader.balance + totalPnl;

    return { positionsWithPnl, totalPnl, totalMargin, equity };
  }, [positions, prices, trader]);
}
